/**
 * Navigation graph containing all walkable nodes with spatial indexing.
 * Provides efficient lookup for pathfinding operations.
 */
export default class NavGraph {
  /**
   * Create a navigation graph
   * @param {number} cellSize - Size of spatial index cells (default 4 units)
   */
  constructor(cellSize = 4) {
    // All nodes indexed by cuboid key
    this.nodes = new Map();

    // Spatial index for efficient neighbor queries
    this.cellSize = cellSize;
    this.spatialIndex = new Map(); // "cellX,cellZ" -> Set<NavNode>
  }

  /**
   * Clear all nodes from the graph
   */
  clear() {
    this.nodes.clear();
    this.spatialIndex.clear();
  }

  /**
   * Add a node to the graph
   * @param {NavNode} node - The node to add
   */
  addNode(node) {
    this.nodes.set(node.cuboidKey, node);
    this._addToSpatialIndex(node);
  }

  /**
   * Remove a node from the graph
   * @param {string} cuboidKey - Key of the node to remove
   */
  removeNode(cuboidKey) {
    const node = this.nodes.get(cuboidKey);
    if (!node) return;

    // Remove from spatial index
    this._removeFromSpatialIndex(node);

    // Remove neighbor references to this node
    for (const neighbor of node.neighbors) {
      neighbor.node.removeNeighbor(node);
    }

    this.nodes.delete(cuboidKey);
  }

  /**
   * Get a node by cuboid key
   * @param {string} cuboidKey - The cuboid key
   * @returns {NavNode|undefined}
   */
  getNode(cuboidKey) {
    return this.nodes.get(cuboidKey);
  }

  /**
   * Get all nodes
   * @returns {IterableIterator<NavNode>}
   */
  getAllNodes() {
    return this.nodes.values();
  }

  /**
   * Get the number of nodes
   * @returns {number}
   */
  get size() {
    return this.nodes.size;
  }

  /**
   * Find the nearest node to a position
   * Uses closest point on surface, not center distance, for accurate results
   * @param {number} x - X coordinate
   * @param {number} y - Y coordinate
   * @param {number} z - Z coordinate
   * @param {number} maxDistance - Maximum search distance (optional)
   * @returns {NavNode|null}
   */
  findNearestNode(x, y, z, maxDistance = Infinity) {
    let nearest = null;
    let nearestDistSq = maxDistance * maxDistance;

    // First, try to find a node that contains this XZ point and closely matches Y
    // Prefer exact Y matches for stacked surfaces
    let bestYMatch = null;
    let bestYDiff = Infinity;

    for (const node of this.nodes.values()) {
      if (node.containsPoint(x, z)) {
        const yDiff = Math.abs(node.y - y);

        // For points at or slightly above the surface, prefer that surface
        // This handles clicks on top faces correctly
        if (y >= node.y - 0.5 && y <= node.y + 1.5) {
          if (yDiff < bestYDiff) {
            bestYDiff = yDiff;
            bestYMatch = node;
          }
        }
      }
    }

    // If found a good Y match within containing nodes, use it
    if (bestYMatch && bestYDiff < 1.5) {
      return bestYMatch;
    }

    // Otherwise, search for any containing node within height tolerance
    for (const node of this.nodes.values()) {
      if (node.containsPoint(x, z) && Math.abs(node.y - y) < 4) {
        // Use closest point distance, not center distance
        const distSq = this._closestPointDistSq(node, x, y, z);
        if (distSq < nearestDistSq) {
          nearestDistSq = distSq;
          nearest = node;
        }
      }
    }

    if (nearest) return nearest;

    // Finally, search all nodes for nearest using closest point distance
    for (const node of this.nodes.values()) {
      const distSq = this._closestPointDistSq(node, x, y, z);
      if (distSq < nearestDistSq) {
        nearestDistSq = distSq;
        nearest = node;
      }
    }

    return nearest;
  }

  /**
   * Calculate squared distance from a point to the closest point on a node's surface
   * @param {NavNode} node - The node
   * @param {number} px - Point X
   * @param {number} py - Point Y
   * @param {number} pz - Point Z
   * @returns {number} Squared distance to closest point on surface
   * @private
   */
  _closestPointDistSq(node, px, py, pz) {
    // Clamp point to node's XZ bounds to find closest point on surface
    const closestX = Math.max(node.minX, Math.min(node.maxX, px));
    const closestZ = Math.max(node.minZ, Math.min(node.maxZ, pz));
    const closestY = node.y;

    const dx = closestX - px;
    const dy = closestY - py;
    const dz = closestZ - pz;
    return dx * dx + dy * dy + dz * dz;
  }

  /**
   * Find all nodes within a radius of a position
   * @param {number} x - X coordinate
   * @param {number} y - Y coordinate
   * @param {number} z - Z coordinate
   * @param {number} radius - Search radius
   * @returns {NavNode[]}
   */
  findNodesInRadius(x, y, z, radius) {
    const results = [];
    const radiusSq = radius * radius;

    // Query spatial index cells
    const cells = this._getCellsInRadius(x, z, radius);

    for (const cellKey of cells) {
      const nodesInCell = this.spatialIndex.get(cellKey);
      if (!nodesInCell) continue;

      for (const node of nodesInCell) {
        const distSq = node.distanceSquaredTo(x, y, z);
        if (distSq <= radiusSq) {
          results.push(node);
        }
      }
    }

    return results;
  }

  /**
   * Find node containing a point (on XZ plane, within height tolerance)
   * @param {number} x - X coordinate
   * @param {number} y - Y coordinate
   * @param {number} z - Z coordinate
   * @param {number} heightTolerance - Maximum Y difference (default 2)
   * @returns {NavNode|null}
   */
  findNodeAtPoint(x, y, z, heightTolerance = 2) {
    const cellKey = this._getCellKey(x, z);
    const adjacentCells = this._getAdjacentCells(
      Math.floor(x / this.cellSize),
      Math.floor(z / this.cellSize)
    );

    let bestNode = null;
    let bestYDiff = heightTolerance;

    for (const key of [cellKey, ...adjacentCells]) {
      const nodesInCell = this.spatialIndex.get(key);
      if (!nodesInCell) continue;

      for (const node of nodesInCell) {
        if (node.containsPoint(x, z)) {
          const yDiff = Math.abs(node.y - y);
          if (yDiff < bestYDiff) {
            bestYDiff = yDiff;
            bestNode = node;
          }
        }
      }
    }

    return bestNode;
  }

  /**
   * Get potential neighbors for a node (nodes in nearby cells)
   * @param {NavNode} node - The node to find neighbors for
   * @param {number} searchRadius - Radius to search (default 16 units)
   * @returns {NavNode[]}
   */
  getPotentialNeighbors(node, searchRadius = 16) {
    const results = [];
    // Search from the node's BOUNDS plus margin, not from center.
    // For large extended cuboids, center-based search misses neighbors at far edges.
    const cells = this._getCellsInBoundsRadius(node, searchRadius);

    const seen = new Set();
    for (const cellKey of cells) {
      const nodesInCell = this.spatialIndex.get(cellKey);
      if (!nodesInCell) continue;

      for (const otherNode of nodesInCell) {
        if (otherNode !== node && !seen.has(otherNode)) {
          seen.add(otherNode);
          results.push(otherNode);
        }
      }
    }

    return results;
  }

  // --- Private spatial index methods ---

  /**
   * Add node to spatial index
   * @private
   */
  _addToSpatialIndex(node) {
    // Add to all cells the node overlaps
    const minCellX = Math.floor(node.minX / this.cellSize);
    const maxCellX = Math.floor(node.maxX / this.cellSize);
    const minCellZ = Math.floor(node.minZ / this.cellSize);
    const maxCellZ = Math.floor(node.maxZ / this.cellSize);

    for (let cx = minCellX; cx <= maxCellX; cx++) {
      for (let cz = minCellZ; cz <= maxCellZ; cz++) {
        const key = `${cx},${cz}`;
        if (!this.spatialIndex.has(key)) {
          this.spatialIndex.set(key, new Set());
        }
        this.spatialIndex.get(key).add(node);
      }
    }
  }

  /**
   * Remove node from spatial index
   * @private
   */
  _removeFromSpatialIndex(node) {
    const minCellX = Math.floor(node.minX / this.cellSize);
    const maxCellX = Math.floor(node.maxX / this.cellSize);
    const minCellZ = Math.floor(node.minZ / this.cellSize);
    const maxCellZ = Math.floor(node.maxZ / this.cellSize);

    for (let cx = minCellX; cx <= maxCellX; cx++) {
      for (let cz = minCellZ; cz <= maxCellZ; cz++) {
        const key = `${cx},${cz}`;
        const set = this.spatialIndex.get(key);
        if (set) {
          set.delete(node);
          if (set.size === 0) {
            this.spatialIndex.delete(key);
          }
        }
      }
    }
  }

  /**
   * Get cell key for a position
   * @private
   */
  _getCellKey(x, z) {
    return `${Math.floor(x / this.cellSize)},${Math.floor(z / this.cellSize)}`;
  }

  /**
   * Get adjacent cell keys
   * @private
   */
  _getAdjacentCells(cellX, cellZ) {
    const cells = [];
    for (let dx = -1; dx <= 1; dx++) {
      for (let dz = -1; dz <= 1; dz++) {
        if (dx !== 0 || dz !== 0) {
          cells.push(`${cellX + dx},${cellZ + dz}`);
        }
      }
    }
    return cells;
  }

  /**
   * Get all cell keys within a radius
   * @private
   */
  _getCellsInRadius(x, z, radius) {
    const cells = new Set();
    const minCellX = Math.floor((x - radius) / this.cellSize);
    const maxCellX = Math.floor((x + radius) / this.cellSize);
    const minCellZ = Math.floor((z - radius) / this.cellSize);
    const maxCellZ = Math.floor((z + radius) / this.cellSize);

    for (let cx = minCellX; cx <= maxCellX; cx++) {
      for (let cz = minCellZ; cz <= maxCellZ; cz++) {
        cells.add(`${cx},${cz}`);
      }
    }

    return cells;
  }

  /**
   * Get all cell keys within a radius of a node's BOUNDS (not center).
   * This ensures large nodes find neighbors at all edges.
   * @param {NavNode} node - The node whose bounds to expand from
   * @param {number} radius - Extra search margin beyond the node's bounds
   * @returns {Set<string>}
   * @private
   */
  _getCellsInBoundsRadius(node, radius) {
    const cells = new Set();
    const minCellX = Math.floor((node.minX - radius) / this.cellSize);
    const maxCellX = Math.floor((node.maxX + radius) / this.cellSize);
    const minCellZ = Math.floor((node.minZ - radius) / this.cellSize);
    const maxCellZ = Math.floor((node.maxZ + radius) / this.cellSize);

    for (let cx = minCellX; cx <= maxCellX; cx++) {
      for (let cz = minCellZ; cz <= maxCellZ; cz++) {
        cells.add(`${cx},${cz}`);
      }
    }

    return cells;
  }
}
